import axios from 'axios';
import type {
  BlacklistStatusResponse,
  LogoutRequest,
  SessionRevocationSummary,
} from '../types/logout.types';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

function getStoredToken(): string | null {
  return (
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token')
  );
}

// Request interceptor — attach bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const logoutApi = {
  /** POST /api/eraf/auth/logout */
  logout: (req: LogoutRequest): Promise<void> =>
    apiClient
      .post<void>('/auth/logout', req)
      .then(() => undefined),

  /** GET /api/eraf/auth/logout/blacklist-status?jti=... */
  getBlacklistStatus: (jti: string): Promise<BlacklistStatusResponse> =>
    apiClient
      .get<unknown, { data: BlacklistStatusResponse }>(
        `/auth/logout/blacklist-status?jti=${encodeURIComponent(jti)}`
      )
      .then((res) => res.data),

  /** GET /api/eraf/auth/logout/sessions/{userId} */
  getActiveSessions: (userId: number): Promise<SessionRevocationSummary> =>
    apiClient
      .get<unknown, { data: SessionRevocationSummary }>(
        `/auth/logout/sessions/${userId}`
      )
      .then((res) => res.data),
};
